import { Site } from '../../../../../types';
import { CheckedIcon, CopyOutlineIcon, OpenInNewIcon } from '@traceair/webapp-icons';
import React, { useMemo, useState } from 'react';
import { classes, stylesheet } from 'typestyle';
import { ButtonIcon, Gray100, Gray120 } from '@traceair/tds';
import * as turf from '@turf/turf';

// The outer container must have a set height, because whiteSpace: nowrap breaks flex layouts, and we have to
// resort to absolute positioning here

const css = stylesheet({
  container: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '100%',
    color: Gray100,
  },
  containerInner: {
    display: 'flex',
    maxWidth: '100%',
    gap: 4,
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    $nest: {
      'svg path': {
        fill: 'currentcolor',
      },
      '&:hover': {
        color: Gray120,
      },
      '&:active': {
        opacity: 0.8,
      },
    },
  },
  containerInnerInOpenMission: {
    $nest: {
      '&&': {
        alignItems: 'flex-start',
      },
    },
  },
  containerInnerInList: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  containerInnerInListRight: {
    left: 'unset',
    right: 0,
  },
  text: {
    flex: '0 1 auto',
  },
  textInList: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  icon: {
    flex: '0 0 auto',
    $nest: {
      svg: {
        display: 'block',
      },
    },
  },
  copyButton: {
    margin: '-5px -2px -5px 8px',
    width: 20,
    minWidth: 20,
    height: 20,
    verticalAlign: -3,
  },
});

interface Props {
  alignRight?: boolean;
  inOpenMission?: boolean;
  site: Site;
}

export const SiteAddress = ({ alignRight = false, inOpenMission, site }: Props) => {
  const [copied, setCopied] = useState(false);

  const link = useMemo(() => {
    if (!site.projectInfo.siteAddress || !site.boundary?.geometry) {
      return null;
    }

    const centroid = turf.centroid(site.boundary);
    const coordinates = centroid.geometry.coordinates;
    const latitude = coordinates[1];
    const longitude = coordinates[0];
    const zoom = 15;

    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}&zoom=${zoom}`;
  }, [site]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    navigator.clipboard.writeText(link ?? '');
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  if (!link) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={css.container}>
      <a
        href={link!}
        target="_blank"
        rel="noreferrer"
        className={classes(
          css.containerInner,
          inOpenMission ? css.containerInnerInOpenMission : css.containerInnerInList,
          alignRight && css.containerInnerInListRight,
        )}
        onClick={handleClick}
        title={site.projectInfo.siteAddress}
      >
        <div className={classes(css.text, !inOpenMission && css.textInList)}>{site.projectInfo.siteAddress}</div>

        <div className={css.icon}>
          <OpenInNewIcon size={16} />
        </div>
      </a>

      {inOpenMission && (
        <div className={css.icon}>
          <ButtonIcon
            className={css.copyButton}
            size="xs"
            style="tertiary"
            icon={copied ? <CheckedIcon size={16} color={Gray100} /> : <CopyOutlineIcon size={16} color={Gray100} />}
            onClick={handleCopy}
          />
        </div>
      )}
    </div>
  );
};
